/*
 * tigerBridgeChromeColor.js
 *
 * iOS Tiger paints native status bar (notch) and bottom home-indicator bar.
 * Same contract as WMS shell (`tigerColorStatusBar` / `tigerBottomBarColor`).
 *
 * Detection: iOS JphIOS (function, dedicated handler, or unified postMessage).
 * Android: only if window.Jph exposes the same functions (feature-detect).
 * Web / tigerSim: no-op.
 */

import { isTigerIOS, isTigerSimActive } from '../tigerDetector';

export const TIGER_COLOR_STATUS_BAR = 'tigerColorStatusBar';
export const TIGER_BOTTOM_BAR_COLOR = 'tigerBottomBarColor';

const CHROME_PRESETS = Object.freeze({
  wms: Object.freeze({
    cssVars: Object.freeze(['--surface-card', '--lp-surface-card', '--jph-white']),
  }),
  portal: Object.freeze({
    cssVars: Object.freeze(['--lp-surface-card']),
  }),
});

/**
 * Light native chrome ↔ dark counterpart (App.css / LoginPortal.css).
 * Status bar and bottom bar follow the surface behind the login page.
 */
const CHROME_THEME_PAIRS = Object.freeze([
  Object.freeze({ light: '#FFFFFF', dark: '#18212F' }),
  Object.freeze({ light: '#F3F8FF', dark: '#1D2736' }),
]);

const FALLBACK = Object.freeze({
  light: '#FFFFFF',
  dark: '#18212F',
});

const toByteHex = (n) => Number(n).toString(16).padStart(2, '0').toUpperCase();

/**
 * Native iOS expects a hex color string (#RRGGBB).
 * @param {unknown} value
 * @returns {string|null}
 */
export function normalizeTigerChromeColor(value) {
  const raw = String(value ?? '').trim();
  if (!raw || raw.toLowerCase() === 'transparent') return null;

  const hex8 = raw.match(/^#?([0-9A-Fa-f]{8})$/);
  if (hex8) return `#${hex8[1].slice(0, 6).toUpperCase()}`;

  const hex6 = raw.match(/^#?([0-9A-Fa-f]{6})$/);
  if (hex6) return `#${hex6[1].toUpperCase()}`;

  const hex3 = raw.match(/^#?([0-9A-Fa-f]{3})$/);
  if (hex3) {
    const [r, g, b] = hex3[1].split('');
    return `#${(r + r + g + g + b + b).toUpperCase()}`;
  }

  const rgbComma = raw.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+)?\s*\)$/i,
  );
  const rgbSpace = raw.match(
    /^rgba?\(\s*(\d{1,3})\s+(\d{1,3})\s+(\d{1,3})(?:\s*\/\s*[\d.]+%?)?\s*\)$/i,
  );
  const rgb = rgbComma || rgbSpace;
  if (!rgb) return null;

  const r = Number(rgb[1]);
  const g = Number(rgb[2]);
  const b = Number(rgb[3]);
  if (![r, g, b].every((n) => Number.isInteger(n) && n >= 0 && n <= 255)) {
    return null;
  }
  return `#${toByteHex(r)}${toByteHex(g)}${toByteHex(b)}`;
}

/**
 * @param {string} varName
 * @returns {string|null}
 */
export function readCssVarColor(varName) {
  if (typeof document === 'undefined' || !varName) return null;
  const name = String(varName).trim();
  if (!name.startsWith('--')) return null;
  try {
    const specified = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    const direct = normalizeTigerChromeColor(specified);
    if (direct) return direct;

    const probe = document.createElement('span');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText = [
      'position:absolute',
      'left:-9999px',
      'top:0',
      'width:1px',
      'height:1px',
      'pointer-events:none',
      `background-color:var(${name})`,
    ].join(';');
    document.documentElement.appendChild(probe);
    const computed = getComputedStyle(probe).backgroundColor;
    probe.remove();
    return normalizeTigerChromeColor(computed);
  } catch {
    return null;
  }
}

export function isDarkTheme() {
  if (typeof document === 'undefined') return false;
  return document.documentElement.getAttribute('data-theme') === 'dark';
}

/**
 * Known light hexes switch to their dark token (and the reverse).
 * Unknown colors pass through so branding is not rewritten.
 * @param {unknown} value
 * @param {boolean} dark
 * @returns {string|null}
 */
export function mapChromeColorToTheme(value, dark) {
  const hex = normalizeTigerChromeColor(value);
  if (!hex) return null;
  for (let i = 0; i < CHROME_THEME_PAIRS.length; i += 1) {
    const pair = CHROME_THEME_PAIRS[i];
    if (hex === pair.light || hex === pair.dark) {
      return dark ? pair.dark : pair.light;
    }
  }
  return hex;
}

function firstCssColor(varNames, fallback) {
  const names = Array.isArray(varNames) ? varNames : [];
  for (let i = 0; i < names.length; i += 1) {
    const color = readCssVarColor(names[i]);
    if (color) return color;
  }
  return fallback;
}

function resolvePreset(preset) {
  const key = preset === 'wms' ? 'wms' : 'portal';
  return CHROME_PRESETS[key];
}

/**
 * @returns {boolean}
 */
export function isTigerChromeColorAvailable() {
  if (typeof window === 'undefined' || isTigerSimActive()) return false;

  if (typeof window.Jph?.[TIGER_COLOR_STATUS_BAR] === 'function') return true;
  if (typeof window.Jph?.[TIGER_BOTTOM_BAR_COLOR] === 'function') return true;
  if (typeof window.JphIOS?.[TIGER_COLOR_STATUS_BAR] === 'function') return true;
  if (typeof window.JphIOS?.[TIGER_BOTTOM_BAR_COLOR] === 'function') return true;

  const handlers = window.webkit?.messageHandlers;
  if (handlers?.[TIGER_COLOR_STATUS_BAR] && typeof handlers[TIGER_COLOR_STATUS_BAR].postMessage === 'function') {
    return true;
  }
  if (handlers?.[TIGER_BOTTOM_BAR_COLOR] && typeof handlers[TIGER_BOTTOM_BAR_COLOR].postMessage === 'function') {
    return true;
  }
  if (isTigerIOS() && typeof handlers?.JphIOS?.postMessage === 'function') {
    return true;
  }
  return false;
}

/**
 * @param {string} methodName
 * @param {unknown} colorString
 * @returns {{ ok: boolean, method?: string, color?: string, error?: string }}
 */
export function invokeTigerChromeColor(methodName, colorString) {
  const color = normalizeTigerChromeColor(colorString);
  if (!color) {
    return { ok: false, error: 'INVALID_COLOR' };
  }
  if (typeof window === 'undefined' || isTigerSimActive()) {
    return { ok: false, error: 'NO_BRIDGE' };
  }

  try {
    if (typeof window.Jph?.[methodName] === 'function') {
      window.Jph[methodName](color);
      return { ok: true, method: `Jph.${methodName}`, color };
    }

    if (typeof window.JphIOS?.[methodName] === 'function') {
      window.JphIOS[methodName](color);
      return { ok: true, method: `JphIOS.${methodName}`, color };
    }

    const dedicated = window.webkit?.messageHandlers?.[methodName];
    if (dedicated && typeof dedicated.postMessage === 'function') {
      dedicated.postMessage(color);
      return { ok: true, method: `webkit.messageHandlers.${methodName}`, color };
    }

    if (isTigerIOS()) {
      const handler = window.webkit?.messageHandlers?.JphIOS;
      if (handler && typeof handler.postMessage === 'function') {
        handler.postMessage({
          action: methodName,
          params: { colorString: color },
        });
        return { ok: true, method: 'webkit.messageHandlers.JphIOS.postMessage', color };
      }
    }
  } catch (err) {
    return { ok: false, error: err?.message || 'INVOKE_FAILED' };
  }

  return { ok: false, error: 'NO_BRIDGE' };
}

/**
 * @param {unknown} colorString
 * @returns {{ ok: boolean, method?: string, color?: string, error?: string }}
 */
export function tigerColorStatusBar(colorString) {
  return invokeTigerChromeColor(TIGER_COLOR_STATUS_BAR, colorString);
}

/**
 * @param {unknown} colorString
 * @returns {{ ok: boolean, method?: string, color?: string, error?: string }}
 */
export function tigerBottomBarColor(colorString) {
  return invokeTigerChromeColor(TIGER_BOTTOM_BAR_COLOR, colorString);
}

/**
 * Theme-aware hex for both native bars. Known light surfaces swap to the
 * dark token (and the reverse) so iOS chrome follows `data-theme`.
 * @param {{
 *   preset?: 'wms' | 'portal',
 *   color?: string,
 *   statusBar?: string,
 *   bottomBar?: string,
 *   cssVars?: string[],
 * }} [options]
 * @returns {{ color: string, statusBar: string, bottomBar: string }}
 */
export function resolveTigerChromeColors(options = {}) {
  const preset = resolvePreset(options.preset);
  const cssVars = options.cssVars || preset.cssVars;
  const dark = isDarkTheme();
  const fallback = dark ? FALLBACK.dark : FALLBACK.light;
  const raw =
    normalizeTigerChromeColor(options.color)
    || normalizeTigerChromeColor(options.statusBar)
    || normalizeTigerChromeColor(options.bottomBar)
    || firstCssColor(cssVars, null);
  const color = mapChromeColorToTheme(raw, dark) || fallback;

  return { color, statusBar: color, bottomBar: color };
}

/**
 * Paints iOS (or Android, if the methods exist) chrome to match the WebView UI.
 * Status bar and bottom bar receive the same theme-mapped hex.
 * @param {{
 *   preset?: 'wms' | 'portal',
 *   color?: string,
 *   statusBar?: string,
 *   bottomBar?: string,
 *   cssVars?: string[],
 * }} [options]
 * @returns {{
 *   ok: boolean,
 *   skipped?: boolean,
 *   status?: { ok: boolean, method?: string, color?: string, error?: string },
 *   bottom?: { ok: boolean, method?: string, color?: string, error?: string },
 * }}
 */
export function syncTigerIosChromeColors(options = {}) {
  if (!isTigerChromeColorAvailable()) {
    return { ok: false, skipped: true };
  }

  const { color } = resolveTigerChromeColors({ preset: 'portal', ...options });
  const status = tigerColorStatusBar(color);
  const bottom = tigerBottomBarColor(color);
  return {
    ok: Boolean(status.ok && bottom.ok),
    status,
    bottom,
  };
}
